import { Link } from '@tanstack/react-router';
import { Fragment } from 'react';
import { cn } from '@/shared/lib/cn';
import type { AreaLink, AreaSwitchItem } from './areaHeaderContext';

/** Pfad oben links im Kopf; jedes Teil außer dem letzten ist klickbar. */
export function AreaCrumbs({ crumbs }: { crumbs: AreaLink[] }) {
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label="Pfad" className="flex min-w-0 flex-wrap items-center gap-2">
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        const className = cn(
          'font-mono text-[11px] font-semibold uppercase tracking-[0.14em] no-underline',
          crumb.current ? 'text-white' : 'text-mint hover:text-white',
        );
        return (
          <Fragment key={`${crumb.to}-${crumb.label}`}>
            {crumb.current ? (
              <span aria-current="page" className={className}>
                {crumb.label}
              </span>
            ) : (
              <Link to={crumb.to} search={crumb.search ?? {}} className={className}>
                {crumb.label}
              </Link>
            )}
            {!last && (
              <span aria-hidden className="text-mint text-[13px]">
                ›
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}

/** Am Handy kürzer (Board „Verfügbarkeit · Handy“), damit alle Bereiche ohne Wischen passen. */
const SHORT_LABEL: Record<string, string> = { 'Verfügbarkeit & Vertrieb': 'Verfügbarkeit' };

/**
 * Bereichsumschalter oben rechts: genau die Unterpunkte des Bereichs aus der Seitenleiste. Bis zu
 * drei Bereiche teilen sich am Handy die Breite zu gleichen Teilen (Board „Preisautomatik · Handy“);
 * mehr bleiben wischbar. Am Handy 44 px hoch (Tippziel), am Desktop wie im Board 36 px.
 */
export function AreaSwitcher({ items }: { items: AreaSwitchItem[] }) {
  if (items.length < 2) return null;
  const even = items.length <= 3;
  return (
    <div
      aria-label="Bereich"
      className={cn(
        'rounded-pill scrollbar-none max-w-full gap-1 overflow-x-auto border border-white/15 bg-white/[0.08] p-1',
        // Gleich breit nur, solange jede Beschriftung hineinpasst; sonst wischbar statt überlappend.
        even ? 'grid auto-cols-[minmax(max-content,1fr)] grid-flow-col md:flex' : 'flex',
      )}
    >
      {items.map((item) => (
        <Link
          key={`${item.to}${JSON.stringify(item.search ?? {})}`}
          to={item.to}
          search={item.search ?? {}}
          aria-current={item.active ? 'page' : undefined}
          className={cn(
            'rounded-pill flex h-11 flex-none items-center gap-2 whitespace-nowrap no-underline md:h-9 md:px-4 md:text-[13px]',
            even ? 'justify-center px-1 text-xs' : 'px-3 text-[13px]',
            item.active
              ? 'text-forest-deep bg-white font-bold'
              : 'font-semibold text-white hover:bg-white/10',
          )}
        >
          {SHORT_LABEL[item.label] ? (
            <>
              <span className="md:hidden">{SHORT_LABEL[item.label]}</span>
              <span className="hidden md:inline">{item.label}</span>
            </>
          ) : (
            item.label
          )}
          {item.count ? (
            <span
              title={item.count === 'error' ? 'Anzahl konnte nicht geladen werden' : undefined}
              className={cn(
                'rounded-pill inline-flex h-5 min-w-5 items-center justify-center px-1.5 text-[11px] font-bold',
                item.count === 'error' ? 'bg-amber-tint text-ink' : 'bg-sealwax text-white',
              )}
            >
              {item.count === 'error' ? '!' : item.count}
            </span>
          ) : null}
        </Link>
      ))}
    </div>
  );
}

/** Reiter unter dem Titel: Unterseiten der gewählten Seite, aktiv in Minze; am Handy 44 px hoch. */
export function AreaTabs({ tabs }: { tabs: AreaSwitchItem[] }) {
  if (tabs.length < 2) return null;
  return (
    <nav aria-label="Reiter" className="scrollbar-none flex max-w-full gap-1 overflow-x-auto">
      {tabs.map((tab) => (
        <Link
          key={`${tab.to}${JSON.stringify(tab.search ?? {})}`}
          to={tab.to}
          search={tab.search ?? {}}
          aria-current={tab.active ? 'page' : undefined}
          className={cn(
            'rounded-pill flex h-11 flex-none items-center whitespace-nowrap px-3 text-[13px] font-bold no-underline md:h-[38px] md:px-4',
            tab.active
              ? 'bg-mint text-forest-deep'
              : 'border border-white/20 text-white hover:bg-white/10',
          )}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}
